import { Box } from "@mui/material";
import { VideoQuality } from "@zoom/videosdk";
import classnames from "classnames";
import {
  CSSProperties,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { ZoomMediaContext } from "../../ZoomMediaProvider";
import Flex from "../../components/Flex";
import { DisclosureHost } from "../../hooks/use-disclosure";
import { RootState } from "../../redux/store";
import { isShallowEqual } from "../../util";
import VideoFooter from "../NewComponents/VideoFooter";
import { useMount, usePrevious } from "../hooks";
import { Participant } from "../index-types";
import { useParticipantsChange } from "../useParticipantsChange";
import { ZoomContext } from "../zoom-context";
import Avatar from "./components/avatar";
import ShareView from "./components/share-view";
import AvatarActionContext from "./context/avatar-context";
import { useAvatarAction } from "./hooks/useAvatarAction";
import { useCanvasDimension } from "./hooks/useCanvasDimension";
import { useNetworkQuality } from "./hooks/useNetworkQuality";
import { SELF_VIDEO_ID } from "./video-constants";
import "./video.scss";
import ZoomStartEncounterBar from "./components/ZoomStartEncounterBar";

interface Props {
  disclosureHost: DisclosureHost;
  hasPatientAttendedCall: boolean;
}

const VideoContainer = ({ disclosureHost, hasPatientAttendedCall }: Props) => {
  const zmClient = useContext(ZoomContext);
  const mediaContext = useContext(ZoomMediaContext);

  if (!mediaContext) {
    throw new Error("ZoomMediaContext is not available. ");
  }

  const {
    mediaStream,
    video: { decode: isVideoDecodeReady },
  } = mediaContext;
  const videoRef = useRef<HTMLCanvasElement | null>(null);
  const selfVideoCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const selfVideoRef = useRef<HTMLVideoElement | null>(null);
  const shareViewRef = useRef<{
    selfShareRef: HTMLCanvasElement | HTMLVideoElement | null;
  }>(null);
  const [isRecieveSharing, setIsRecieveSharing] = useState(false);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [activeVideo, setActiveVideo] = useState<number>(
    mediaStream?.getActiveVideoId() ?? 0
  );
  const previousActiveUser = useRef<Participant>();
  const canvasDimension = useCanvasDimension(mediaStream, videoRef);
  const selfCanvasDimension = useCanvasDimension(
    mediaStream,
    selfVideoCanvasRef
  );
  if (!zmClient) {
    throw new Error("zmClient is not available. ");
  }

  const networkQuality = useNetworkQuality(zmClient);
  const previousCanvasDimension = usePrevious(canvasDimension);

  useParticipantsChange(zmClient, (payload) => {
    setParticipants(payload);
  });

  const onActiveVideoChange = useCallback((payload: any) => {
    const { userId } = payload;
    setActiveVideo(userId);
  }, []);
  const renderSelfView = useCallback(
    (isCapturing: boolean) => {
      const element = (
        mediaStream?.isRenderSelfViewWithVideoElement()
          ? selfVideoRef.current
          : selfVideoCanvasRef.current
      ) as HTMLVideoElement | HTMLCanvasElement | null;

      if (!element) return;

      if (isCapturing) {
        mediaStream?.renderVideo(
          element,
          zmClient?.getSessionInfo().userId,
          element?.width,
          element?.height,
          0,
          0,
          3
        );
      } else {
        mediaStream?.stopRenderVideo(element, zmClient?.getSessionInfo().userId);
      }
    },
    [mediaStream, zmClient]
  );

  const onCurrentVideoChange = useCallback(
    (payload: any) => {
      renderSelfView(payload.state === "Started");
    },
    [renderSelfView]
  );

  // The preview hands over a camera that is already capturing, so the
  // `video-capturing-change` that attaches self view fires before this mounts.
  useMount(() => {
    if (mediaStream?.isCapturingVideo()) {
      renderSelfView(true);
    }
  });

  useEffect(() => {
    zmClient?.on("video-active-change", onActiveVideoChange);
    zmClient?.on("video-capturing-change", onCurrentVideoChange);
    return () => {
      zmClient?.off("video-active-change", onActiveVideoChange);
      zmClient?.off("video-capturing-change", onCurrentVideoChange);
    };
  }, [zmClient, onActiveVideoChange, onCurrentVideoChange]);
  // active user = regard as `video-active-change` payload, excluding the case where it is self and the video is turned on.
  // In this case, the self video is rendered seperately.
  const activeUser = useMemo(
    () =>
      participants.find(
        (user) =>
          user.userId === activeVideo &&
          !(user.userId === zmClient?.getSessionInfo().userId && user.bVideoOn)
      ),
    [participants, activeVideo, zmClient]
  );
  const isCurrentUserStartedVideo = zmClient?.getCurrentUserInfo()?.bVideoOn;

  useEffect(() => {
    if (mediaStream && videoRef.current) {
      if (activeUser?.bVideoOn !== previousActiveUser.current?.bVideoOn) {
        if (activeUser?.bVideoOn) {
          mediaStream.renderVideo(
            videoRef.current,
            activeUser.userId,
            canvasDimension.width,
            canvasDimension.height,
            0,
            0,
            VideoQuality.Video_360P as any
          );
        } else {
          if (previousActiveUser.current?.bVideoOn) {
            mediaStream.stopRenderVideo(
              videoRef.current,
              previousActiveUser.current?.userId
            );
          }
        }
      }
      if (activeUser?.bVideoOn && previousActiveUser.current?.bVideoOn) {
        if (activeUser.userId !== previousActiveUser.current.userId) {
          mediaStream.stopRenderVideo(
            videoRef.current,
            previousActiveUser.current?.userId
          );
          mediaStream.renderVideo(
            videoRef.current,
            activeUser.userId,
            canvasDimension.width,
            canvasDimension.height,
            0,
            0,
            VideoQuality.Video_360P as any
          );
        } else {
          if (!isShallowEqual(canvasDimension, previousCanvasDimension)) {
            mediaStream.adjustRenderedVideoPosition(
              videoRef.current,
              activeUser.userId,
              canvasDimension.width,
              canvasDimension.height,
              0,
              0
            );
          }
        }
      }
      previousActiveUser.current = activeUser;
    }
  }, [
    mediaStream,
    activeUser,
    isVideoDecodeReady,
    canvasDimension,
    previousCanvasDimension,
    zmClient,
    isCurrentUserStartedVideo,
  ]);

  useEffect(() => {
    if (
      selfVideoCanvasRef.current &&
      selfCanvasDimension.width > 0 &&
      selfCanvasDimension.height > 0 &&
      isCurrentUserStartedVideo
    ) {
      mediaStream?.adjustRenderedVideoPosition(
        selfVideoCanvasRef.current,
        zmClient.getSessionInfo().userId,
        selfCanvasDimension.width,
        selfCanvasDimension.height,
        0,
        0
      );
    }
  }, [selfCanvasDimension, mediaStream, zmClient, isCurrentUserStartedVideo]);

  const avatarActionState = useAvatarAction(
    zmClient,
    activeUser ? [activeUser] : []
  );

  const isMinimizeCall = useSelector(
    (state: RootState) => state.joinRoom.isMinimize
  );

  const shouldApplyStyle = <T,>(c: T): T | undefined =>
    !isMinimizeCall ? c : undefined;

  const selfVideoStyle: CSSProperties =
    participants.length > 1
      ? {
          position: "absolute",
          top: isMinimizeCall ? "10%" : "76%",
          left: isMinimizeCall ? 2 : undefined,
        }
      : { position: "absolute", top: 28 };

  return (
    <Box
      className={shouldApplyStyle("viewport")}
      sx={{
        boxSizing: "border-box",
        ...(isMinimizeCall && {
          padding: 0,
          margin: 0,
          overflow: "hidden",
        }),
      }}
      // position={"absolute"}
      height={"100%"}
      width={"100%"}
    >
      {!isMinimizeCall && zmClient?.isHost() && (
        <ZoomStartEncounterBar zmClient={zmClient} />
      )}

      <ShareView
        ref={shareViewRef}
        onRecieveSharingChange={setIsRecieveSharing}
      />

      <Flex
        flexDirection={"column"}
        height={shouldApplyStyle("calc(100vh - 40px)") ?? "100%"}
        position={isRecieveSharing ? "absolute" : "inherit"}
        top={isMinimizeCall ? 0 : 25}
        width={"100%"}
        alignItems={"center"}
        justifyContent={"center"}
        className={shouldApplyStyle(
          classnames("video-container", "single-video-container", {
            "video-container-in-sharing": isRecieveSharing,
          })
        )}
        bgcolor={"transparent"}
        borderRadius={9}
        sx={{
          ...(isMinimizeCall && {
            padding: 0,
            margin: 0,
          }),
        }}
      >
        {mediaStream?.isRenderSelfViewWithVideoElement() ? (
          <video
            style={{
              borderRadius: 9,
              zIndex: 99,
              display: mediaStream?.isCapturingVideo() ? "block" : "none",
              ...selfVideoStyle,
            }}
            height={
              isMinimizeCall
                ? participants?.length === 1
                  ? "80%"
                  : "25%"
                : undefined
            }
            id={SELF_VIDEO_ID}
            autoPlay
            muted
            playsInline
            className={classnames("self-video", {
              "single-self-video": participants.length === 1,
              "self-video-show": isCurrentUserStartedVideo,
            })}
            ref={selfVideoRef}
          />
        ) : (
          <canvas
            id={SELF_VIDEO_ID + " canvas"}
            style={{
              width: isMinimizeCall
                ? participants?.length === 1
                  ? "90%"
                  : "30%"
                : undefined,
              height: isMinimizeCall
                ? participants?.length === 1
                  ? "80%"
                  : "30%"
                : undefined,
              borderRadius: 9,
              zIndex: 998,
              display: mediaStream?.isCapturingVideo() ? "block" : "none",
              ...selfVideoStyle,
            }}
            className={classnames("self-video", {
              "single-self-video": participants.length === 1,
              "self-video-show": isCurrentUserStartedVideo,
            })}
            ref={selfVideoCanvasRef}
          />
        )}

        <div
          className={shouldApplyStyle("single-video-wrap")}
          style={{
            ...(isMinimizeCall && {
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }),
          }}
        >
          <canvas
            className={classnames("video-canvas")}
            style={{
              borderRadius: 10,
              height: "100%",
              ...(isMinimizeCall
                ? isRecieveSharing
                  ? SHARE_DEFAULT_STYLE
                  : CANVAS_DEFAULT_STYLE
                : isRecieveSharing
                  ? SHARE_OTHER_SCREEN_STYLE
                  : {}),
            }}
            id="video-canvas"
            width={shouldApplyStyle("800")}
            height={shouldApplyStyle("600")}
            ref={videoRef}
          />

          {!isRecieveSharing && (
            <AvatarActionContext.Provider value={avatarActionState}>
              {activeUser && (
                <Avatar
                  style={AVATAR_DEFAULT_STYLE}
                  participant={activeUser}
                  isActive={false}
                  className={shouldApplyStyle("single-view-avatar")}
                  networkQuality={networkQuality[`${activeUser.userId}`]}
                />
              )}
            </AvatarActionContext.Provider>
          )}
        </div>
      </Flex>
      <VideoFooter
        hasPatientAttendedCall={hasPatientAttendedCall}
        className={shouldApplyStyle("video-operations")}
        sharing
        selfShareCanvas={shareViewRef.current?.selfShareRef}
        disclosureHost={disclosureHost}
      />
    </Box>
  );
};

export default VideoContainer;

const CANVAS_DEFAULT_STYLE: CSSProperties = {
  zIndex: 99,
  position: "relative",
  width: "100%",
  height: "100%",
  maxWidth: "100%",
  maxHeight: "100%",
};

const SHARE_DEFAULT_STYLE: CSSProperties = {
  zIndex: 99,
  position: "absolute",
  top: "38%",
  right: "70%",
  width: "8.8em",
  height: "6em",
  borderRadius: 9,
};

const SHARE_OTHER_SCREEN_STYLE: CSSProperties = {
  zIndex: 99,
  position: "relative",
  top: 100,
  right: 20,
  width: "16em",
  height: "10em",
};

const AVATAR_DEFAULT_STYLE: CSSProperties = {
  width: "100%",
  height: "20px",
  top: "86%",
  backgroundColor: "transparent",
};
